/**
 * NewSupplierShortcut.js
 *
 * Componente único, sin omisiones, que:
 * - Toma el modo (light/dark) del theme padre (useTheme).
 * - Crea un customTheme según ese modo.
 * - Dentro de un <ThemeProvider>, se monta un segundo componente
 *   que llama a useTheme para consumir ese customTheme.
 * - Sin llamadas a hooks dentro de callbacks/IIFEs en el render.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  IconButton,
  Paper
} from '@mui/material';
import {
  createTheme,
  ThemeProvider,
  styled,
  useTheme
} from '@mui/material/styles';
import {
  Search as SearchIcon,
  RefreshCw,
  TrendingUp,
  ExternalLink,
  HelpCircle as HelpIcon
} from 'lucide-react';

// Servicio para la búsqueda simulada
import searchService from '../services/AGTUps-product-services/searchService';

/* ---------------------------------------------------------
   1) Paleta de colores y función para crear theme personalizado
   --------------------------------------------------------- */
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

const createCustomTheme = (mode, actualMode) =>
  createTheme({
    palette: { mode, actualMode },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          contained: {
            backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
            color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
            },
            '&:disabled': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.12)'
            }
          },
          text: {
            color: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.text[mode]
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'),
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.25)' : undefined,
              backdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
              WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
              '& fieldset': {
                borderColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined
              },
              '& input, & textarea': {
                color: actualMode === 'wallpaper' ? '#FFFFFF' : undefined
              }
            },
            '& .MuiInputLabel-root': {
              color: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.7)' : undefined
            }
          }
        }
      }
    }
  });

/* ---------------------------------------------------------
   2) Helpers y estilos reutilizados
   --------------------------------------------------------- */
const navigationColors = (theme) => ({
  background:
    theme.palette.mode === 'dark'
      ? 'rgba(40, 40, 42, 0.94)'
      : 'rgba(242, 242, 247, 1)',
  textDefault: theme.palette.mode === 'dark' ? '#FFFFFF' : '#666666',
  textPlaceholder:
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#666666',
  iconColor: 'rgba(128, 128, 128, 0.5)'
});

// Contenedor principal (con scroll interno si hace falta).
const StyledContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: '100%',
  overflowY: 'auto',
  paddingBottom: '16px'
}));

// Barra de búsqueda con estilos unificados
const StyledSearchBar = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: navigationColors(theme).background,
    '& fieldset': { border: 'none' },
    '& input': {
      color: navigationColors(theme).textDefault,
      '&::placeholder': {
        color: navigationColors(theme).textPlaceholder,
        opacity: 1
      }
    }
  }
}));

// Botón de acción con estilo
const StyledActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: navigationColors(theme).iconColor,
  borderRadius: '30px',
  color: '#FFFFFF',
  textTransform: 'none',
  padding: '6px 16px',
  fontWeight: 500,
  boxShadow: 'none',
  transition: 'none',
  '&:hover': {
    backgroundColor: 'rgba(128, 128, 128, 0.7)'
  }
}));

// Tarjeta para mostrar resultados de búsqueda
const StyledResultCard = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  padding: '16px',
  backgroundColor: navigationColors(theme).background,
  color: navigationColors(theme).textDefault,
  boxShadow: 'none',
  marginBottom: '16px'
}));


/* ---------------------------------------------------------
   3) Componente interno que consume el customTheme
      (obtenido de <ThemeProvider theme={customTheme}>)
   --------------------------------------------------------- */
function NewSupplierShortcutContent({ open, onClose }) {
  // Aquí, useTheme() nos devuelve el "customTheme"
  const theme = useTheme();

  // Estados de búsqueda
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Tendencias de ejemplo
  const trendingSearches = [
    { label: 'Proveedores de Acero', icon: <TrendingUp size={16} /> },
    { label: 'Paneles Solares', icon: <TrendingUp size={16} /> },
    { label: 'Maquinaria Pesada', icon: <TrendingUp size={16} /> }
  ];

  // Handler para buscar
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    try {
      const response = await searchService.createSearch(searchQuery);
      if (response.success) {
        setResults(response.data.results || []);
      }
      setHasSearched(true);
    } catch (error) {
      console.error('[NewSupplierShortcut] handleSearch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handler para refrescar
  const handleRefresh = () => {
    setSearchQuery('');
    setResults([]);
    setHasSearched(false);
  };

  // Renderizado de la "landing" (antes de buscar)
  const renderLanding = () => (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        pt: 2,
        pb: 4
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 'bold',
          textAlign: 'center',
          mb: 3,
          [theme.breakpoints.down('sm')]: {
            fontSize: '1.75rem'
          }
        }}
      >
        ¿Qué proveedor buscas hoy?
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 600 }}>
        <StyledSearchBar
          fullWidth
          placeholder="Busca proveedores, productos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
        />
      </Box>

      <StyledActionButton onClick={handleSearch} disabled={loading}>
        {loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon size={16} />}
        <span style={{ marginLeft: 8 }}>
          {loading ? 'Buscando...' : 'Buscar'}
        </span>
      </StyledActionButton>

      {/* Tendencias */}
      <Box
        sx={{
          mt: 2,
          textAlign: 'center',
          opacity: loading ? 0.3 : 1,
          transition: 'opacity 0.5s ease-in-out'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontWeight: 500,
            [theme.breakpoints.down('sm')]: {
              fontSize: '1.1rem'
            }
          }}
        >
          Tendencias de búsqueda
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'center'
          }}
        >
          {trendingSearches.map((trend, idx) => (
            <Button
              key={idx}
              variant="outlined"
              onClick={() => {
                setSearchQuery(trend.label);
                handleSearch();
              }}
              startIcon={trend.icon}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
                borderColor: theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.3)' : undefined,
                [theme.breakpoints.down('sm')]: {
                  fontSize: '0.9rem',
                  padding: '4px 8px'
                }
              }}
            >
              {trend.label}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // Renderizado de los resultados (después de buscar)
  const renderResults = () => (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Resultados de Búsqueda
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <StyledSearchBar
              fullWidth
              placeholder="Busca proveedores, productos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </Grid>

          <Grid item>
            <StyledActionButton onClick={handleSearch} disabled={loading}>
              {loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SearchIcon size={16} />
              )}
              <span style={{ marginLeft: 8 }}>
                {loading ? 'Buscando...' : 'Buscar'}
              </span>
            </StyledActionButton>
          </Grid>

          <Grid item>
            <IconButton onClick={handleRefresh}>
              {loading ? <CircularProgress size={20} /> : <RefreshCw size={20} />}
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && results.length === 0 && (
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          No hay resultados. Intenta con otra búsqueda.
        </Typography>
      )}

      {!loading &&
        results.map((item, idx) => (
          <StyledResultCard key={idx}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {item.title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              {item.snippet}
            </Typography>

            {item.relevanceReason && (
              <Box
                sx={{
                  mt: 2,
                  borderLeft: `4px solid ${navigationColors(theme).iconColor}`,
                  paddingLeft: 1
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontStyle: 'italic',
                    color: navigationColors(theme).textPlaceholder
                  }}
                >
                  <strong>AGT dice:</strong> {item.relevanceReason}
                </Typography>
              </Box>
            )}

            <Box sx={{ textAlign: 'right', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => window.open(item.link, '_blank')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  borderColor: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.3)'
                    : navigationColors(theme).iconColor,
                  color: theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : navigationColors(theme).iconColor,
                  boxShadow: 'none',
                  transition: 'none',
                  '&:hover': {
                    borderColor: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.5)'
                      : 'rgba(128, 128, 128, 0.7)'
                  }
                }}
                startIcon={<ExternalLink size={16} />}
              >
                Visitar
              </Button>
            </Box>
          </StyledResultCard>
        ))}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="new-supplier-dialog-title"
    >
      <DialogTitle
        id="new-supplier-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            New Supplier
          </Typography>
          <IconButton size="small">
            <HelpIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <StyledContainer>
          {hasSearched ? renderResults() : renderLanding()}
        </StyledContainer>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{
            color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined
          }}
        >
          Close
        </Button>
        <Button
          onClick={() => {
            alert('Crear proveedor manualmente');
          }}
          variant="contained"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}


/* ---------------------------------------------------------
   4) Componente principal que exportamos
   ---------------------------------------------------------
   - Lee el modo del theme padre (useTheme())
   - Crea el customTheme
   - Monta un <ThemeProvider> con ese customTheme
   - Renderiza el contenido real (NewSupplierShortcutContent) dentro
*/
export default function NewSupplierShortcut({ open, onClose }) {
  // 1) Tomamos el modo del theme padre
  const parentTheme = useTheme();
  // 2) Creamos el customTheme con ese modo
  const customTheme = createCustomTheme(parentTheme.palette.mode, parentTheme.palette.actualMode);

  // 3) Retornamos un ThemeProvider con el customTheme
  //    y dentro usamos NewSupplierShortcutContent
  return (
    <ThemeProvider theme={customTheme}>
      <NewSupplierShortcutContent open={open} onClose={onClose} />
    </ThemeProvider>
  );
}
